import { useState } from 'react'
import { useAuth } from '../../state/auth'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card } from '../../components/ui/card'
import { motion } from 'framer-motion'
import type { RegisterRequest } from '../../models/auth'
import { useMutationTracker } from '@/hooks/useMutationTracker'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '../../hooks/useTranslation'
import { Eye, EyeOff } from 'lucide-react'
import { ParticleNetwork } from '@/components/ui/particle-network'

const getErrorMessage = (error: unknown): string => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return 'An error occurred'
}

export default function RegisterPage() {
  const code = new URLSearchParams(window.location.search).get('ref') || '';
  const { register, registerLoading, registerError } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState<RegisterRequest>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    referral_code: code || '',
  })

  useMutationTracker('register').addListener(({isSuccess}) => {
    if (isSuccess) window.location.href = '/dashboard';
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    register(formData)
  }

  const handleChange = (field: keyof RegisterRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Particle Network Background */}
      <div className="absolute inset-0 z-0">
        <ParticleNetwork className="w-full h-full" />
      </div>

      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0 bg-[#080c15]/60 z-[1]" />

      <div className="relative z-10 w-full flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <Card className="p-8 bg-[#0a0f1c]/90 border-[#0dcbfc]/20 backdrop-blur-md shadow-[0_0_50px_-12px_rgba(13,203,252,0.25)]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r from-[#0dcbfc] to-[#0dcbfc]/80 text-white mb-4 shadow-[0_0_30px_-5px_rgba(13,203,252,0.5)] overflow-hidden">
                <img src="/nexa-logo.jpg" alt="Nexa" className="w-full h-full object-cover" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-2 text-white">{t('pages.auth.register.title')}</h1>
              <p className="text-[#0dcbfc]/70">{t('pages.auth.register.subtitle')}</p>
            </motion.div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Input
                  type="text"
                  placeholder={t('pages.auth.register.firstNamePlaceholder')}
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20"
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Input
                  type="email"
                  placeholder={t('pages.auth.register.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20"
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="relative"
              >
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t('pages.auth.register.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0dcbfc]/60 hover:text-[#0dcbfc] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="relative"
              >
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t('pages.auth.register.confirmPasswordPlaceholder')}
                  value={formData.password_confirmation}
                  onChange={handleChange('password_confirmation')}
                  required
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0dcbfc]/60 hover:text-[#0dcbfc] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Input
                  type="text"
                  disabled={code !== ''}
                  placeholder={t('pages.auth.register.referralCodePlaceholder')}
                  value={formData.referral_code}
                  onChange={handleChange('referral_code')}
                  className="bg-[#080c15]/50 border-[#0dcbfc]/30 text-white placeholder:text-[#0dcbfc]/40 focus:border-[#0dcbfc] focus:ring-[#0dcbfc]/20 disabled:opacity-50"
                />
              </motion.div>

              {registerError ? (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-3 rounded-xl"
                >
                  {getErrorMessage(registerError) || t('common.messages.error')}
                </motion.div>
              ) : null}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-[#0dcbfc] hover:bg-[#0dcbfc]/90 text-[#080c15] font-semibold shadow-[0_0_20px_-5px_rgba(13,203,252,0.4)] hover:shadow-[0_0_30px_-5px_rgba(13,203,252,0.6)] transition-all"
                  disabled={registerLoading}
                >
                  {registerLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin w-4 h-4 border-2 border-[#080c15] border-t-transparent rounded-full" />
                      <span>{t('common.messages.loading')}</span>
                    </div>
                  ) : (
                    t('pages.auth.register.createAccount')
                  )}
                </Button>
              </motion.div>
            </form>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-6 text-center"
            >
              <p className="text-sm text-[#0dcbfc]/60">
                {t('pages.auth.register.alreadyHaveAccount')}{' '}
                <button 
                  onClick={() => navigate({ to: '/login' })}
                  className="text-[#0dcbfc] hover:text-[#0dcbfc]/80 font-medium transition-colors underline-offset-4 hover:underline"
                >
                  {t('pages.auth.register.signIn')}
                </button>
              </p>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
